import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useDataCache } from '../context/DataCacheContext';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/apiClient';
import { formatNumber } from '../utils/currencyFormatter';
import {
  ArrowLeft, Plus, Trash2, Upload, DollarSign, TrendingDown, PieChart,
  FileSpreadsheet, Pencil, X, Check, Package, Search, Filter, RefreshCw,
  Save, CheckCircle2, AlertCircle, Layers, ChevronDown, ChevronUp, Link as LinkIcon,
  Percent
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import TutorialVideoButton from '../components/TutorialVideoButton';

const CATEGORIES = ['Nomina', 'Arriendo', 'Servicios', 'Empaque', 'Transporte', 'Publicidad', 'Software', 'Impuestos', 'Otros'];

// Función de formato que será reemplazada con el site del usuario en el componente principal
let formatCurrency = (v) => formatNumber(v, 'MCO');

// ========================================
// SUB-COMPONENTE: OperationalExpenses (existente)
// ========================================
const OperationalExpenses = ({ expenses, summary, onAdd, onDelete, onEdit, onCsvUpload, onDownloadTemplate, taxes = [], onAddTax, onDeleteTax }) => {
  const [concept, setConcept] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('Otros');
  const [type, setType] = useState('fixed');
  const [taxConcept, setTaxConcept] = useState('');
  const [taxPercentage, setTaxPercentage] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editConcept, setEditConcept] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editType, setEditType] = useState('');

  const handleAdd = () => {
    if (!concept || !amount || parseFloat(amount) <= 0) { 
      toast.error('Completa concepto y monto'); 
      return; 
    }
    onAdd({ concept, amount: parseFloat(amount), category, type });
    setConcept(''); 
    setAmount('');
  };

  const startEdit = (e) => {
    setEditId(e.expense_id);
    setEditConcept(e.concept);
    setEditAmount(String(e.amount));
    setEditCategory(e.category);
    setEditType(e.type);
  };

  const cancelEdit = () => setEditId(null);

  const saveEdit = () => {
    onEdit(editId, {
      concept: editConcept,
      amount: parseFloat(editAmount),
      category: editCategory,
      type: editType
    });
    setEditId(null);
  };

  const handleCsv = () => {
    if (!csvFile) { toast.error('Selecciona un archivo'); return; }
    onCsvUpload(csvFile);
    setCsvFile(null);
  };

  const handleAddTax = () => {
    const pct = parseFloat(taxPercentage);
    if (!taxConcept.trim()) { toast.error('Ingresa el concepto del impuesto'); return; }
    if (!pct || pct <= 0 || pct > 100) { toast.error('El porcentaje debe estar entre 0 y 100'); return; }
    onAddTax({ concept: taxConcept.trim(), percentage: pct });
    setTaxConcept('');
    setTaxPercentage('');
  };

  const cats = summary?.by_category || {};
  const totalExpenses = summary?.total || 0;

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-rose-600/10 to-rose-800/10 border border-rose-500/30 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown className="w-5 h-5 text-rose-600 dark:text-rose-400" />
            <span className="text-sm text-rose-700 dark:text-rose-300">Gastos Fijos (mes)</span>
          </div>
          <p className="text-2xl font-bold text-rose-600 dark:text-rose-400">${formatCurrency(summary?.fixed_total)}</p>
        </div>
        <div className="bg-gradient-to-br from-amber-600/10 to-amber-800/10 border border-amber-500/30 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            <span className="text-sm text-amber-700 dark:text-amber-300">Gastos Variables</span>
          </div>
          <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">${formatCurrency(summary?.variable_total)}</p>
        </div>
        <div className="bg-gradient-to-br from-red-600/10 to-red-800/10 border border-red-500/30 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <PieChart className="w-5 h-5 text-red-600 dark:text-red-400" />
            <span className="text-sm text-red-700 dark:text-red-300">Total Costos</span>
          </div>
          <p className="text-2xl font-bold text-red-600 dark:text-red-400">${formatCurrency(totalExpenses)}</p>
        </div>
      </div>

      {/* Distribución por categoría */}
      {Object.keys(cats).length > 0 && (
        <div className="bg-card/60 border border-border rounded-2xl p-5">
          <h3 className="text-base font-semibold text-foreground mb-4">Distribucion por Categoria</h3>
          <div className="space-y-3">
            {Object.entries(cats).map(([cat, val]) => {
              const pct = totalExpenses > 0 ? (val / totalExpenses * 100) : 0;
              return (
                <div key={cat}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">{cat}</span>
                    <span className="text-foreground font-medium">${formatCurrency(val)} ({pct.toFixed(1)}%)</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-rose-500 to-amber-500 rounded-full transition-all" style={{ width: pct + '%' }}></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Formulario */}
      <div className="bg-card/60 border border-border rounded-2xl p-5">
        <h3 className="text-base font-semibold text-foreground mb-4">Registrar Gasto</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-start">
          <Input value={concept} onChange={e => setConcept(e.target.value)} placeholder="Concepto" className="bg-background/50 border-border text-foreground" data-testid="expense-concept" />
          <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Monto" className="bg-background/50 border-border text-foreground" data-testid="expense-amount" />
          <select value={category} onChange={e => setCategory(e.target.value)} className="bg-background/50 border border-border rounded-md text-foreground text-sm px-3 h-10" data-testid="expense-category">
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <div>
            <select value={type} onChange={e => setType(e.target.value)} className="bg-background/50 border border-border rounded-md text-foreground text-sm px-3 h-10 w-full" data-testid="expense-type">
              <option value="fixed">Fijo (mensual)</option>
              <option value="variable">Variable (por orden)</option>
            </select>
          </div>
          <Button onClick={handleAdd} className="bg-emerald-600 hover:bg-emerald-500 h-10" data-testid="expense-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Agregar
          </Button>
        </div>
      </div>

      {/* Carga CSV */}
      <div className="bg-card/60 border border-border rounded-2xl p-5">
        <h3 className="text-base font-semibold text-foreground mb-3">Carga Masiva</h3>
        <div className="flex flex-wrap gap-3 items-center">
          <Button onClick={onDownloadTemplate} className="bg-slate-700 hover:bg-slate-600" data-testid="download-template-btn">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Descargar Plantilla
          </Button>
          <div className="h-6 border-l border-border"></div>
          <input type="file" accept=".csv,.xlsx" onChange={e => setCsvFile(e.target.files[0])} className="text-sm text-muted-foreground file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-muted file:text-muted-foreground" />
          <Button onClick={handleCsv} disabled={!csvFile} className="bg-blue-600 hover:bg-blue-500">
            <Upload className="w-4 h-4 mr-1" /> Subir
          </Button>
        </div>
      </div>

      {/* Impuestos */}
      <div className="bg-card/60 border border-blue-500/30 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Percent className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <h3 className="text-base font-semibold text-foreground">Impuestos (% sobre ventas)</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Registra impuestos como un porcentaje mensual aplicado sobre tus ventas reales. Se mostrarán en el Dashboard como deducción.
        </p>

        {/* Formulario */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start mb-4">
          <Input
            value={taxConcept}
            onChange={e => setTaxConcept(e.target.value)}
            placeholder="Concepto (ej: IVA, Renta)"
            className="bg-background/50 border-border text-foreground"
            data-testid="tax-concept"
          />
          <div className="relative">
            <Input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={taxPercentage}
              onChange={e => setTaxPercentage(e.target.value)}
              placeholder="Porcentaje (%)"
              className="bg-background/50 border-border text-foreground pr-8"
              data-testid="tax-percentage"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
          </div>
          <Button onClick={handleAddTax} className="bg-blue-600 hover:bg-blue-500 h-10" data-testid="tax-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Agregar Impuesto
          </Button>
        </div>

        {/* Lista de impuestos */}
        {taxes.length === 0 ? (
          <p className="text-muted-foreground/70 text-sm text-center py-4">No hay impuestos registrados</p>
        ) : (
          <div className="space-y-2">
            {taxes.map((t) => (
              <div key={t.tax_id} className="flex items-center justify-between bg-muted rounded-lg px-4 py-3">
                <div>
                  <span className="text-foreground font-medium">{t.concept}</span>
                  <span className="ml-3 text-blue-600 dark:text-blue-400 font-bold">{t.percentage}%</span>
                  <span className="ml-2 text-xs text-muted-foreground/70">mensual sobre ventas</span>
                </div>
                <button onClick={() => onDeleteTax(t.tax_id)} className="text-muted-foreground/70 hover:text-red-600 dark:hover:text-red-400 ml-4">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lista de gastos */}
      <div className="bg-card/60 border border-border rounded-2xl p-5">
        <h3 className="text-base font-semibold text-foreground mb-4">Gastos Registrados ({expenses.length})</h3>
        {expenses.length === 0 ? (
          <p className="text-muted-foreground/70 text-center py-8">No hay gastos registrados</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-muted-foreground">
                  <th className="text-left py-2 px-2">Concepto</th>
                  <th className="text-left py-2 px-2">Categoria</th>
                  <th className="text-left py-2 px-2">Tipo</th>
                  <th className="text-right py-2 px-2">Monto</th>
                  <th className="text-center py-2 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((e, i) => (
                  <tr key={i} className="border-b border-border hover:bg-muted/30">
                    {editId === e.expense_id ? (
                      <>
                        <td className="py-2 px-2"><Input value={editConcept} onChange={ev => setEditConcept(ev.target.value)} className="bg-background/50 border-border text-foreground h-8 text-sm" /></td>
                        <td className="py-2 px-2">
                          <select value={editCategory} onChange={ev => setEditCategory(ev.target.value)} className="bg-background/50 border border-border rounded-md text-foreground text-xs px-2 h-8 w-full">
                            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </td>
                        <td className="py-2 px-2">
                          <select value={editType} onChange={ev => setEditType(ev.target.value)} className="bg-background/50 border border-border rounded-md text-foreground text-xs px-2 h-8 w-full">
                            <option value="fixed">Fijo /mes</option>
                            <option value="variable">Variable /orden</option>
                          </select>
                        </td>
                        <td className="py-2 px-2"><Input type="number" value={editAmount} onChange={ev => setEditAmount(ev.target.value)} className="bg-background/50 border-border text-foreground h-8 text-sm text-right" /></td>
                        <td className="py-2 px-2 text-center">
                          <div className="flex gap-1 justify-center">
                            <button onClick={saveEdit} className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"><Check className="w-4 h-4" /></button>
                            <button onClick={cancelEdit} className="text-muted-foreground/70 hover:text-muted-foreground"><X className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-2 px-2 text-foreground">{e.concept}</td>
                        <td className="py-2 px-2 text-muted-foreground">{e.category}</td>
                        <td className="py-2 px-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${e.type === 'fixed' ? 'bg-blue-500/20 text-blue-600 dark:text-blue-400' : 'bg-amber-500/20 text-amber-600 dark:text-amber-400'}`}>
                            {e.type === 'fixed' ? 'Fijo /mes' : 'Variable /orden'}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-right font-medium text-rose-600 dark:text-rose-400">${formatCurrency(e.amount)}</td>
                        <td className="py-2 px-2 text-center">
                          <div className="flex gap-2 justify-center">
                            <button onClick={() => startEdit(e)} className="text-muted-foreground/70 hover:text-blue-600 dark:hover:text-blue-400"><Pencil className="w-4 h-4" /></button>
                            <button onClick={() => onDelete(e.expense_id)} className="text-muted-foreground/70 hover:text-red-600 dark:hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ========================================
// SUB-COMPONENTE: Costos de Producto (COGS)
// ========================================
const CostosProducto = ({ products, loadingProducts, onSaveCogs, onUploadCogs, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'title', direction: 'asc' });

  // Estadísticas
  const stats = useMemo(() => {
    if (!products || products.length === 0) {
      return { total: 0, withCogs: 0, withoutCogs: 0, progress: 0 };
    }
    const withCogs = products.filter(p => p.cogs && p.cogs > 0).length;
    const withoutCogs = products.length - withCogs;
    const progress = Math.round((withCogs / products.length) * 100);
    return { total: products.length, withCogs, withoutCogs, progress };
  }, [products]);

  // Filtrar y ordenar productos
  const filteredProducts = useMemo(() => {
    let result = [...(products || [])];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(p => 
        (p.title || '').toLowerCase().includes(term) ||
        (p.sku || '').toLowerCase().includes(term) ||
        (p.item_id || '').toLowerCase().includes(term)
      );
    }
    
    if (filter === 'with_cogs') {
      result = result.filter(p => p.cogs && p.cogs > 0);
    } else if (filter === 'without_cogs') {
      result = result.filter(p => !p.cogs || p.cogs === 0);
    }
    
    // Sort: inactivos al final, luego por el criterio seleccionado
    result.sort((a, b) => {
      // Inactivos al final
      const statusOrder = { active: 0, under_review: 1, paused: 2, closed: 3 };
      const aStatus = statusOrder[a.status] ?? (a.status ? 2 : 0);
      const bStatus = statusOrder[b.status] ?? (b.status ? 2 : 0);
      if (aStatus !== bStatus) return aStatus - bStatus;
      
      let aVal = a[sortConfig.key] || '';
      let bVal = b[sortConfig.key] || '';
      if (sortConfig.key === 'cogs') {
        aVal = a.cogs || 0;
        bVal = b.cogs || 0;
      }
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [products, searchTerm, filter, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      await onUploadCogs(uploadFile);
      setUploadFile(null);
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await apiClient.get('/api/products/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_costos_productos.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Error al descargar plantilla');
    }
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return null;
    return sortConfig.direction === 'asc' ? 
      <ChevronUp className="w-3 h-3" /> : 
      <ChevronDown className="w-3 h-3" />;
  };

  // Fila de producto editable
  const ProductRow = ({ product }) => {
    const [cogs, setCogs] = useState(product.cogs || '');
    const [hasChanged, setHasChanged] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const inputRef = React.useRef(null);
    
    useEffect(() => {
      setCogs(product.cogs || '');
      setHasChanged(false);
    }, [product.cogs]);
    
    // Cuando entra en modo edición, enfocar el input
    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, [isEditing]);
    
    const handleChange = (value) => {
      // Solo permitir números y punto decimal
      const numericValue = value.replace(/[^0-9.]/g, '');
      setCogs(numericValue);
      setHasChanged(parseFloat(numericValue) !== (product.cogs || 0));
    };
    
    const handleSave = async () => {
      if (hasChanged) {
        setSavingId(product.item_id + (product.variation_id || ''));
        await onSaveCogs(product.item_id, product.variation_id, parseFloat(cogs) || 0, product.sku);
        setHasChanged(false);
        setSavingId(null);
      }
      setIsEditing(false);
    };
    
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        handleSave();
      } else if (e.key === 'Escape') {
        setCogs(product.cogs || '');
        setHasChanged(false);
        setIsEditing(false);
      }
    };
    
    const hasCogs = product.cogs && product.cogs > 0;
    const isSaving = savingId === (product.item_id + (product.variation_id || ''));
    const isInactive = product.status && product.status !== 'active';
    
    // Formatear número para mostrar
    const formatDisplayCogs = (value) => {
      if (!value && value !== 0) return '-';
      return new Intl.NumberFormat('es-CO').format(value);
    };
    
    return (
      <tr className={`border-b border-border/50 hover:bg-muted/30 transition-colors ${!hasCogs ? 'bg-red-950/10' : ''} ${isInactive ? 'opacity-50' : ''}`}>
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-3">
            {product.thumbnail ? (
              <img src={product.thumbnail} alt="" className="w-9 h-9 rounded-lg object-cover bg-muted" />
            ) : (
              <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center">
                <Package className="w-4 h-4 text-muted-foreground/70" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm text-foreground font-medium truncate max-w-[250px]" title={product.title}>
                {product.title || 'Sin título'}
              </p>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-mono text-muted-foreground/70">{product.item_id}</span>
                {isInactive && <span className="px-1.5 py-0.5 bg-slate-500/20 text-muted-foreground rounded text-[9px] font-medium">{product.status === 'closed' ? 'CERRADA' : product.status === 'paused' ? 'PAUSADA' : String(product.status).toUpperCase()}</span>}
              </div>
            </div>
          </div>
        </td>
        <td className="px-3 py-2.5">
          <span className="text-xs font-mono text-muted-foreground">{product.sku || '-'}</span>
        </td>
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-2 justify-center">
            {isEditing || !hasCogs ? (
              // Modo edición: Input
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground/70 text-xs">$</span>
                <Input
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  value={cogs}
                  onChange={(e) => handleChange(e.target.value)}
                  onBlur={handleSave}
                  onKeyDown={handleKeyDown}
                  placeholder="0"
                  className={`w-28 h-8 text-right text-sm pl-5 pr-2 ${
                    !hasCogs && !cogs 
                      ? 'border-red-500/50 bg-red-500/10' 
                      : hasChanged 
                        ? 'border-amber-500/50 bg-amber-500/10' 
                        : 'border-border bg-muted'
                  }`}
                  data-testid={`cogs-input-${product.item_id}`}
                />
              </div>
            ) : (
              // Modo visualización: Click para editar
              <button
                onClick={() => setIsEditing(true)}
                onDoubleClick={() => setIsEditing(true)}
                className="group flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-muted hover:bg-slate-700 border border-border hover:border-emerald-500/50 transition-all cursor-pointer min-w-[100px] justify-end"
                title="Haz clic para editar"
                data-testid={`cogs-display-${product.item_id}`}
              >
                <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">$ {formatDisplayCogs(product.cogs)}</span>
                <Pencil className="w-3 h-3 text-muted-foreground/70 group-hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors" />
              </button>
            )}
            {hasChanged && !isSaving && isEditing && (
              <Button size="sm" onClick={handleSave} className="h-8 px-2 bg-emerald-600 hover:bg-emerald-500">
                <Save className="w-3 h-3" />
              </Button>
            )}
            {isSaving && <RefreshCw className="w-4 h-4 text-blue-600 dark:text-blue-400 animate-spin" />}
          </div>
        </td>
        <td className="px-3 py-2.5 text-center">
          {hasCogs ? (
            <CheckCircle2 className="w-4 h-4 text-green-700 dark:text-green-500 mx-auto" />
          ) : (
            <AlertCircle className="w-4 h-4 text-red-700 dark:text-red-500 mx-auto" />
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header de Progreso */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 bg-card/60 border border-border/50 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-xl ${stats.progress === 100 ? 'bg-green-500/20' : 'bg-amber-500/20'}`}>
                {stats.progress === 100 ? <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" /> : <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />}
              </div>
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Progreso de Costos</h3>
                <p className="text-[10px] text-muted-foreground/70">Productos con COGS asignado</p>
              </div>
            </div>
            <span className={`text-2xl font-bold ${stats.progress === 100 ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>{stats.progress}%</span>
          </div>
          <div className="h-2.5 bg-muted rounded-full overflow-hidden mb-2">
            <div 
              className={`h-full rounded-full transition-all duration-500 ${
                stats.progress === 100 ? 'bg-gradient-to-r from-green-500 to-emerald-400' : 
                stats.progress >= 70 ? 'bg-gradient-to-r from-amber-500 to-yellow-400' :
                'bg-gradient-to-r from-red-500 to-orange-400'
              }`}
              style={{ width: `${stats.progress}%` }}
            />
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-green-600 dark:text-green-400">{stats.withCogs} con costo</span>
            <span className="text-red-600 dark:text-red-400">{stats.withoutCogs} sin costo</span>
          </div>
        </div>
        
        <div className="bg-card/60 border border-border/50 rounded-2xl p-5 flex flex-col justify-center">
          <div className="flex items-center gap-2 mb-2">
            <Package className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span className="text-xs text-muted-foreground">Total Productos</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{stats.total}</p>
        </div>
      </div>

      {/* Carga Masiva */}
      <div className="bg-card/60 border border-border rounded-2xl p-5">
        <h3 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
          <Upload className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          Carga Masiva de Costos
        </h3>
        <p className="text-xs text-muted-foreground mb-4">Descarga la plantilla con tus productos, completa la columna COGS y subela.</p>
        <div className="flex flex-wrap gap-3 items-center">
          <Button onClick={handleDownloadTemplate} variant="outline" size="sm" className="border-border">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Descargar Plantilla
          </Button>
          <div className="h-6 border-l border-border" />
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => setUploadFile(e.target.files[0])}
            className="text-sm text-muted-foreground file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-muted file:text-muted-foreground"
            data-testid="upload-cogs-file"
          />
          <Button onClick={handleUpload} disabled={!uploadFile || uploading} className="bg-emerald-600 hover:bg-emerald-500">
            {uploading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Subir COGS
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/70" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por titulo, SKU o ID..."
            className="pl-10 bg-card/60 border-border"
            data-testid="search-products"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground/70" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-card/60 border border-border rounded-lg text-sm text-foreground px-3 py-2"
            data-testid="filter-cogs"
          >
            <option value="all">Todos ({stats.total})</option>
            <option value="without_cogs">Sin COGS ({stats.withoutCogs})</option>
            <option value="with_cogs">Con COGS ({stats.withCogs})</option>
          </select>
        </div>
        <Button onClick={onRefresh} variant="outline" size="sm" className="border-border" disabled={loadingProducts}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loadingProducts ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Tabla de Productos */}
      <div className="bg-card/60 border border-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="w-full text-sm" data-testid="costs-table">
            <thead className="bg-muted/70 sticky top-0 z-10">
              <tr className="text-muted-foreground text-xs">
                <th className="px-3 py-3 text-left cursor-pointer hover:text-foreground" onClick={() => handleSort('title')}>
                  <div className="flex items-center gap-1">Producto <SortIcon column="title" /></div>
                </th>
                <th className="px-3 py-3 text-left cursor-pointer hover:text-foreground" onClick={() => handleSort('sku')}>
                  <div className="flex items-center gap-1">SKU <SortIcon column="sku" /></div>
                </th>
                <th className="px-3 py-3 text-center cursor-pointer hover:text-foreground" onClick={() => handleSort('cogs')}>
                  <div className="flex items-center gap-1 justify-center">COGS (Costo) <SortIcon column="cogs" /></div>
                </th>
                <th className="px-3 py-3 text-center">Estado</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => (
                <ProductRow key={`${product.item_id}-${product.variation_id || 'main'}`} product={product} />
              ))}
            </tbody>
          </table>
          
          {filteredProducts.length === 0 && (
            <div className="text-center py-12 text-muted-foreground/70">
              <Package className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>{searchTerm || filter !== 'all' ? 'No se encontraron productos' : 'No hay productos'}</p>
            </div>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
        <p className="text-xs text-blue-700 dark:text-blue-300">
          <strong>Importante:</strong> El COGS que ingreses aqui se usara en el <strong>Dashboard</strong>, <strong>Inventario</strong> y <strong>Rentabilidad</strong> para calcular tu margen real.
        </p>
      </div>
    </div>
  );
};

// ========================================
// SUB-COMPONENTE: Calculadora de Rentabilidad v2 (Colombia)
// ========================================
const CalculadoraRentabilidad = () => {
  const [mode, setMode] = useState('analyze'); // 'analyze' o 'suggest'
  const [loading, setLoading] = useState(false);
  const [loadingUrl, setLoadingUrl] = useState(false);
  const [result, setResult] = useState(null);
  
  // Inputs comunes
  const [price, setPrice] = useState('');
  const [productCost, setProductCost] = useState('');
  const [productUrl, setProductUrl] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [categoryName, setCategoryName] = useState('');
  const [extractedInfo, setExtractedInfo] = useState(null);
  const [listingType, setListingType] = useState('gold_pro'); // Premium por defecto
  const [deductIva, setDeductIva] = useState(false);
  const [deductRetention, setDeductRetention] = useState(false);
  
  // Inputs modo sugerir
  const [desiredMargin, setDesiredMargin] = useState('20');
  
  // Detectar zona de peligro
  const isDangerZone = (p) => p >= 55000 && p <= 65000;
  
  // Extraer categoría desde URL
  const handleExtractFromUrl = async (url) => {
    if (!url || url.length < 10) return;
    
    setLoadingUrl(true);
    try {
      const response = await apiClient.post('/api/calculator/extract-from-url', { url });
      if (response.data.success) {
        setCategoryId(response.data.category_id);
        setCategoryName(response.data.category_name);
        setExtractedInfo(response.data);
        toast.success(`Categoría detectada: ${response.data.category_name}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo detectar la categoría del link');
      setExtractedInfo(null);
    } finally {
      setLoadingUrl(false);
    }
  };
  
  // Limpiar categoría y URL
  const clearCategory = () => {
    setCategoryId('');
    setCategoryName('');
    setProductUrl('');
    setExtractedInfo(null);
  };
  
  // Analizar precio
  const handleAnalyze = async () => {
    if (!price || parseFloat(price) <= 0) {
      toast.error('Ingresa un precio válido');
      return;
    }
    if (!productCost || parseFloat(productCost) <= 0) {
      toast.error('Ingresa el costo del producto');
      return;
    }
    if (!categoryId && !productUrl) {
      toast.warning('Pega un link de producto similar para obtener la comisión exacta');
    }
    
    setLoading(true);
    try {
      const response = await apiClient.post('/api/calculator/profitability', {
        price: parseFloat(price),
        product_cost: parseFloat(productCost),
        category_id: categoryId,
        product_url: productUrl,
        listing_type_id: listingType,
        deduct_iva: deductIva,
        deduct_retention: deductRetention
      });
      setResult(response.data);
      
      // Si se extrajo info de URL, actualizar
      if (response.data.extracted_info) {
        setCategoryId(response.data.extracted_info.detected_category_id);
        setCategoryName(response.data.extracted_info.detected_category_name);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al calcular rentabilidad');
    } finally {
      setLoading(false);
    }
  };
  
  // Sugerir precio
  const handleSuggestPrice = async () => {
    if (!productCost || parseFloat(productCost) <= 0) {
      toast.error('Ingresa el costo del producto');
      return;
    }
    if (!categoryId && !productUrl) {
      toast.error('Pega un link de producto similar para calcular el precio óptimo');
      return;
    }
    
    setLoading(true);
    try {
      const response = await apiClient.post('/api/calculator/suggest-price', {
        product_cost: parseFloat(productCost),
        desired_margin_pct: parseFloat(desiredMargin),
        category_id: categoryId,
        product_url: productUrl,
        listing_type_id: listingType,
        deduct_iva: deductIva,
        deduct_retention: deductRetention
      });
      setResult(response.data);
      
      // Si se extrajo info de URL, actualizar
      if (response.data.extracted_info) {
        setCategoryId(response.data.extracted_info.detected_category_id);
        setCategoryName(response.data.extracted_info.detected_category_name);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al calcular precio');
    } finally {
      setLoading(false);
    }
  };
  
  // Componente de fila de desglose
  const BreakdownRow = ({ label, value, isNegative = true, highlight = false, subtext = null }) => (
    <div className={`flex justify-between items-center py-2.5 px-3 ${highlight ? 'bg-muted/70 rounded-lg' : 'border-b border-border/50'}`}>
      <div>
        <span className={highlight ? 'text-foreground font-medium' : 'text-muted-foreground text-sm'}>{label}</span>
        {subtext && <span className="text-muted-foreground/70 text-xs ml-2">({subtext})</span>}
      </div>
      <span className={`font-medium ${
        highlight ? 'text-foreground text-lg' : 
        isNegative ? 'text-red-600 dark:text-red-400' : 'text-foreground'
      }`}>
        {isNegative && value > 0 ? '-' : ''}{formatCurrency(Math.abs(value))}
      </span>
    </div>
  );
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <PieChart className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
            Calculadora de Rentabilidad
          </h2>
          <p className="text-xs text-muted-foreground/70">Módulo definitivo para Mercado Libre Colombia</p>
        </div>
      </div>

      {/* Mode Toggle */}
      <div className="flex gap-2 bg-muted p-1 rounded-lg w-fit" data-testid="calculator-mode-toggle">
        <button
          onClick={() => { setMode('analyze'); setResult(null); }}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
            mode === 'analyze' 
              ? 'bg-cyan-500/20 text-cyan-600 dark:text-cyan-400 border border-cyan-500/30' 
              : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="mode-analyze-btn"
        >
          Analizar Precio
        </button>
        <button
          onClick={() => { setMode('suggest'); setResult(null); }}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
            mode === 'suggest' 
              ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30' 
              : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="mode-suggest-btn"
        >
          Sugerir Precio
        </button>
      </div>
      
      {/* Alerta Zona de Peligro */}
      {mode === 'analyze' && price && isDangerZone(parseFloat(price)) && (
        <div className="p-4 bg-red-500/20 border-2 border-red-500/50 rounded-xl animate-pulse" data-testid="danger-zone-alert">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-600 dark:text-red-400 font-bold text-sm">ZONA DE PELIGRO</p>
              <p className="text-red-700 dark:text-red-300 text-xs mt-1">
                Al pasar de $59.900 a $60.000 se activa el envío gratis obligatorio. 
                Verifica si ganas más vendiendo a $59.900 (sin costo de envío).
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Formulario de Inputs */}
        <div className="bg-card/60 rounded-xl border border-border p-5 space-y-4">
          <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
            <Package className="w-4 h-4 text-muted-foreground" />
            {mode === 'analyze' ? 'Datos de la Publicación' : 'Calcular Precio Óptimo'}
          </h3>
          
          {/* Costo del Producto (COGS) */}
          <div>
            <label className="text-sm text-muted-foreground mb-1.5 block">Costo de Mercancía (COGS)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70">$</span>
              <Input
                type="number"
                value={productCost}
                onChange={(e) => setProductCost(e.target.value)}
                placeholder="Ej: 25000"
                className="pl-7 bg-muted border-border"
                data-testid="input-product-cost"
              />
            </div>
            <p className="text-[10px] text-muted-foreground/70 mt-1">Costo del producto puesto en bodega</p>
          </div>
          
          {/* Precio de Venta (solo en modo análisis) */}
          {mode === 'analyze' && (
            <div>
              <label className="text-sm text-muted-foreground mb-1.5 block">Precio de Venta Tentativo</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70">$</span>
                <Input
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="Ej: 89900"
                  className="pl-7 bg-muted border-border"
                  data-testid="input-price"
                />
              </div>
            </div>
          )}
          
          {/* Margen Deseado (solo en modo sugerir) */}
          {mode === 'suggest' && (
            <div>
              <label className="text-sm text-muted-foreground mb-1.5 block">Margen Neto Deseado</label>
              <div className="relative">
                <Input
                  type="number"
                  value={desiredMargin}
                  onChange={(e) => setDesiredMargin(e.target.value)}
                  placeholder="20"
                  className="bg-muted border-border pr-8"
                  data-testid="input-desired-margin"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/70">%</span>
              </div>
            </div>
          )}
          
          {/* Tipo de Publicación */}
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Tipo de Publicación</label>
            <div className="flex gap-3">
              <button
                onClick={() => setListingType('gold_pro')}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all border ${
                  listingType === 'gold_pro'
                    ? 'bg-amber-500/20 text-amber-600 dark:text-amber-400 border-amber-500/50'
                    : 'bg-muted text-muted-foreground border-border hover:border-border'
                }`}
                data-testid="listing-type-premium"
              >
                Premium
                <span className="block text-xs opacity-70 mt-0.5">Mayor exposición</span>
              </button>
              <button
                onClick={() => setListingType('gold_special')}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all border ${
                  listingType === 'gold_special'
                    ? 'bg-slate-500/20 text-muted-foreground border-slate-500/50'
                    : 'bg-muted text-muted-foreground border-border hover:border-border'
                }`}
                data-testid="listing-type-classic"
              >
                Clásica
                <span className="block text-xs opacity-70 mt-0.5">Menor comisión</span>
              </button>
            </div>
          </div>
          
          {/* Link de Producto Similar - NUEVO */}
          <div>
            <label className="text-sm text-muted-foreground mb-1.5 block flex items-center gap-2">
              <LinkIcon className="w-4 h-4" />
              Pegar Link de Producto Similar
            </label>
            <p className="text-[10px] text-muted-foreground/70 mb-2">
              Pega el link de un producto similar de MercadoLibre para detectar automáticamente la categoría y comisión
            </p>
            
            {/* Categoría detectada */}
            {categoryId && categoryName ? (
              <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg mb-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium truncate">Categoría: {categoryName}</p>
                  {extractedInfo?.competitor_info?.title && (
                    <p className="text-[10px] text-muted-foreground/70 truncate mt-0.5">
                      Producto ref: {extractedInfo.competitor_info.title}
                    </p>
                  )}
                </div>
                <button 
                  onClick={clearCategory}
                  className="text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : null}
            
            {/* Input de URL */}
            {!categoryId && (
              <div className="relative">
                <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/70" />
                <Input
                  type="text"
                  value={productUrl}
                  onChange={(e) => {
                    setProductUrl(e.target.value);
                  }}
                  onBlur={(e) => {
                    if (e.target.value) handleExtractFromUrl(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && productUrl) {
                      e.preventDefault();
                      handleExtractFromUrl(productUrl);
                    }
                  }}
                  placeholder="https://articulo.mercadolibre.com.co/MCO-123456..."
                  className="pl-9 pr-20 bg-muted border-border"
                  data-testid="input-product-url"
                />
                {loadingUrl ? (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <RefreshCw className="w-4 h-4 animate-spin text-cyan-600 dark:text-cyan-400" />
                  </div>
                ) : productUrl && (
                  <button
                    onClick={() => handleExtractFromUrl(productUrl)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-xs bg-cyan-500/20 text-cyan-600 dark:text-cyan-400 rounded hover:bg-cyan-500/30"
                  >
                    Detectar
                  </button>
                )}
              </div>
            )}
          </div>
          
          {/* Interruptores Fiscales */}
          <div className="pt-2 border-t border-border">
            <label className="text-sm text-muted-foreground mb-3 block">Configuración Fiscal</label>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={deductIva}
                  onChange={(e) => setDeductIva(e.target.checked)}
                  className="w-4 h-4 rounded bg-muted border-border text-cyan-700 dark:text-cyan-500 focus:ring-cyan-500"
                  data-testid="toggle-iva"
                />
                <div>
                  <span className="text-sm text-muted-foreground group-hover:text-foreground">Descontar IVA (19%)</span>
                  <p className="text-[10px] text-muted-foreground/70">Si eres responsable de IVA</p>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={deductRetention}
                  onChange={(e) => setDeductRetention(e.target.checked)}
                  className="w-4 h-4 rounded bg-muted border-border text-cyan-700 dark:text-cyan-500 focus:ring-cyan-500"
                  data-testid="toggle-retention"
                />
                <div>
                  <span className="text-sm text-muted-foreground group-hover:text-foreground">Descontar Retención (4%)</span>
                  <p className="text-[10px] text-muted-foreground/70">Retención en la fuente por ventas</p>
                </div>
              </label>
            </div>
          </div>
          
          {/* Botón de acción */}
          <Button
            onClick={mode === 'analyze' ? handleAnalyze : handleSuggestPrice}
            disabled={loading}
            className="w-full bg-cyan-600 hover:bg-cyan-700 h-11 mt-2"
            data-testid="calculate-btn"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <PieChart className="w-4 h-4 mr-2" />
            )}
            {mode === 'analyze' ? 'Calcular Rentabilidad' : 'Calcular Precio Óptimo'}
          </Button>
        </div>
        
        {/* Panel de Resultados */}
        <div className="space-y-4">
          {!result ? (
            <div className="bg-card/60 rounded-xl border border-border p-8 text-center">
              <PieChart className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
              <p className="text-muted-foreground/70">Ingresa los datos y calcula</p>
              <p className="text-xs text-muted-foreground/70 mt-2">
                Obtendrás un desglose completo de costos y rentabilidad
              </p>
            </div>
          ) : mode === 'analyze' ? (
            /* Resultados de Análisis - Vista Cascada */
            <>
              {/* Warning de comisión si no hay categoría o si hay error */}
              {result.commission_info?.warning && (
                <div className="p-3 bg-amber-500/20 border border-amber-500/40 rounded-xl" data-testid="commission-warning">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-amber-700 dark:text-amber-300">{result.commission_info.warning}</p>
                      {result.commission_info.source === 'api_no_category' && (
                        <p className="text-[10px] text-amber-600/70 dark:text-amber-400/70 mt-1">
                          Tip: Selecciona una categoría específica (hoja) para obtener la comisión exacta de tu producto.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Warning si no hay categoría seleccionada */}
              {!result.inputs.category_id && !result.commission_info?.warning && (
                <div className="p-3 bg-amber-500/20 border border-amber-500/40 rounded-xl" data-testid="no-category-warning">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      Selecciona una categoría para obtener la comisión exacta. Se está usando la tarifa base de MercadoLibre.
                    </p>
                  </div>
                </div>
              )}
              
              {/* Alerta de zona de peligro */}
              {result.alerts?.danger_zone && (
                <div className="p-3 bg-red-500/20 border border-red-500/40 rounded-xl" data-testid="result-danger-alert">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                    <p className="text-xs text-red-700 dark:text-red-300">{result.alerts.danger_zone_message}</p>
                  </div>
                </div>
              )}
              
              {/* Header del Resultado con Precio y Utilidad Neta */}
              <div className="grid grid-cols-2 gap-3" data-testid="result-header">
                {/* Precio Analizado */}
                <div className="bg-gradient-to-br from-cyan-600/20 to-blue-600/20 rounded-xl border border-cyan-500/30 p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs text-cyan-700 dark:text-cyan-300">Precio Analizado</p>
                      <p className="text-2xl font-bold text-foreground">{formatCurrency(result.inputs.price)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">{result.inputs.listing_type}</p>
                      <p className="text-xs text-muted-foreground/70">
                        {result.commission_info?.source === 'api' ? '✓ API MeLi' : 
                         result.commission_info?.source === 'api_no_category' ? '✓ Base API' : 
                         '⚠️ Sin API'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Tarifa detectada */}
                  {(result.commission_info?.source === 'api' || result.commission_info?.source === 'api_no_category') && result.commission_info?.detected_rate > 0 && (
                    <div className="mt-3 pt-3 border-t border-cyan-500/20">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                          <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium truncate">
                            {result.commission_info.source === 'api' 
                              ? `Tarifa: "${result.commission_info.category_name}"`
                              : 'Tarifa base MeLi'}
                          </span>
                        </div>
                        <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                          {result.commission_info.detected_rate}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* UTILIDAD NETA FINAL - Ahora junto al precio */}
                <div className={`rounded-xl p-4 border-2 flex flex-col justify-center ${
                  result.results.net_profit >= 0 
                    ? 'bg-emerald-500/10 border-emerald-500/50' 
                    : 'bg-red-500/10 border-red-500/50'
                }`}>
                  <p className="text-xs text-muted-foreground mb-1">MARGEN NETO</p>
                  <p className={`text-3xl font-bold ${result.results.net_margin_pct >= 20 ? 'text-emerald-600 dark:text-emerald-400' : result.results.net_margin_pct >= 10 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>
                    {result.results.net_margin_pct}%
                  </p>
                  <p className={`text-lg font-semibold mt-2 ${result.results.net_profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                    {formatCurrency(result.results.net_profit)} utilidad
                  </p>
                </div>
              </div>
              
              {/* Desglose Cascada */}
              <div className="bg-card/60 rounded-xl border border-border overflow-hidden" data-testid="result-breakdown">
                {/* (+) Precio de Venta */}
                <div className="p-3 bg-green-500/10 border-b border-border">
                  <div className="flex justify-between items-center">
                    <span className="text-green-600 dark:text-green-400 font-medium text-sm">(+) Precio de Venta</span>
                    <span className="text-green-600 dark:text-green-400 font-bold text-lg">{formatCurrency(result.inputs.price)}</span>
                  </div>
                </div>
                
                {/* (-) Costos Directos */}
                <div className="border-b border-border">
                  <div className="p-3 bg-muted/30">
                    <span className="text-rose-600 dark:text-rose-400 font-medium text-sm">(-) Costos Directos</span>
                  </div>
                  <div className="px-2">
                    <BreakdownRow label="Costo de Mercancía" value={result.breakdown.direct_costs.product_cost} />
                    <BreakdownRow 
                      label="Comisión MeLi" 
                      value={result.breakdown.direct_costs.commission_amount}
                      subtext={`${result.breakdown.direct_costs.commission_pct}%`}
                    />
                    {result.breakdown.direct_costs.unit_fee > 0 && (
                      <BreakdownRow label="Costo Fijo Unitario" value={result.breakdown.direct_costs.unit_fee} />
                    )}
                    <BreakdownRow label="Subtotal Directos" value={result.breakdown.direct_costs.subtotal} highlight />
                  </div>
                </div>
                
                {/* (-) Logística */}
                <div className="border-b border-border">
                  <div className="p-3 bg-muted/30">
                    <span className="text-amber-600 dark:text-amber-400 font-medium text-sm">(-) Logística</span>
                  </div>
                  <div className="px-2">
                    {result.breakdown.logistics.shipping_cost > 0 && (
                      <BreakdownRow label="Envío Gratis Obligatorio" value={result.breakdown.logistics.shipping_cost} />
                    )}
                    <BreakdownRow 
                      label="Reserva Devoluciones" 
                      value={result.breakdown.logistics.returns_reserve}
                      subtext={`${result.breakdown.logistics.returns_reserve_pct}%`}
                    />
                    <BreakdownRow 
                      label="Fondo Defectuosos" 
                      value={result.breakdown.logistics.defective_fund}
                      subtext={`${result.breakdown.logistics.defective_fund_pct}%`}
                    />
                    <BreakdownRow label="Subtotal Logística" value={result.breakdown.logistics.subtotal} highlight />
                  </div>
                </div>
                
                {/* (-) Impuestos */}
                {(result.breakdown.taxes.iva_amount > 0 || result.breakdown.taxes.retention_amount > 0) && (
                  <div className="border-b border-border">
                    <div className="p-3 bg-muted/30">
                      <span className="text-blue-600 dark:text-blue-400 font-medium text-sm">(-) Impuestos</span>
                    </div>
                    <div className="px-2">
                      {result.breakdown.taxes.iva_amount > 0 && (
                        <BreakdownRow 
                          label="IVA" 
                          value={result.breakdown.taxes.iva_amount}
                          subtext={`${result.breakdown.taxes.iva_pct}%`}
                        />
                      )}
                      {result.breakdown.taxes.retention_amount > 0 && (
                        <BreakdownRow 
                          label="Retención en la Fuente" 
                          value={result.breakdown.taxes.retention_amount}
                          subtext={`${result.breakdown.taxes.retention_pct}%`}
                        />
                      )}
                      <BreakdownRow label="Subtotal Impuestos" value={result.breakdown.taxes.subtotal} highlight />
                    </div>
                  </div>
                )}
              </div>
              
              {/* Info de umbrales */}
              <div className="flex flex-wrap gap-2">
                {result.alerts.applies_free_shipping && (
                  <span className="px-2 py-1 bg-blue-500/20 text-blue-600 dark:text-blue-400 text-xs rounded-lg">
                    Aplica envío gratis obligatorio
                  </span>
                )}
                {result.alerts.applies_unit_fee && (
                  <span className="px-2 py-1 bg-amber-500/20 text-amber-600 dark:text-amber-400 text-xs rounded-lg">
                    Aplica costo fijo unitario
                  </span>
                )}
              </div>
            </>
          ) : (
            /* Resultados de Sugerencia de Precio */
            <>
              {/* Precio Sugerido Principal con tarifa detectada */}
              <div className="bg-gradient-to-br from-emerald-600/20 to-green-600/20 rounded-xl border-2 border-emerald-500/50 p-5 text-center" data-testid="suggested-price-result">
                <p className="text-sm text-emerald-600 dark:text-emerald-400 mb-1">Precio Sugerido de Venta</p>
                <p className="text-4xl font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(result.suggested_price)}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  Para obtener un margen neto del {result.inputs.desired_margin_pct}%
                </p>
                
                {/* Tarifa detectada */}
                {result.commission_info?.source === 'api' && result.commission_info?.detected_rate > 0 && (
                  <div className="mt-3 pt-3 border-t border-emerald-500/20">
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-700 dark:text-emerald-300" />
                      <span className="text-xs text-emerald-700 dark:text-emerald-300">
                        Tarifa para "{result.commission_info.category_name}": <strong>{result.commission_info.detected_rate}%</strong>
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Alternativa (si aplica) */}
              {result.alternative && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4" data-testid="alternative-price">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-amber-600 dark:text-amber-400 font-medium text-sm mb-1">Alternativa Recomendada</p>
                      <p className="text-xs text-amber-700/80 dark:text-amber-300/80 mb-2">{result.alternative.reason}</p>
                      <div className="flex items-center justify-between bg-card/60 rounded-lg p-3">
                        <div>
                          <p className="text-xs text-muted-foreground">Precio</p>
                          <p className="text-lg font-bold text-foreground">{formatCurrency(result.alternative.price)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Ganancia</p>
                          <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(result.alternative.profit)}</p>
                          <p className="text-xs text-emerald-600 dark:text-emerald-400">{result.alternative.margin_pct}% margen</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Desglose del precio sugerido */}
              <div className="bg-card/60 rounded-xl border border-border p-4 space-y-2" data-testid="suggested-breakdown">
                <p className="text-sm text-muted-foreground font-medium mb-3">Desglose del Precio</p>
                
                <BreakdownRow label="Costo de Mercancía" value={result.breakdown.product_cost} isNegative={false} />
                <BreakdownRow 
                  label="Comisión MeLi" 
                  value={result.breakdown.commission}
                  subtext={`${result.breakdown.commission_pct}%`}
                />
                {result.breakdown.unit_fee > 0 && (
                  <BreakdownRow label="Costo Fijo Unitario" value={result.breakdown.unit_fee} />
                )}
                {result.breakdown.shipping_cost > 0 && (
                  <BreakdownRow label="Envío Gratis" value={result.breakdown.shipping_cost} />
                )}
                <BreakdownRow label="Reserva Devoluciones" value={result.breakdown.returns_reserve} subtext="3%" />
                <BreakdownRow label="Fondo Defectuosos" value={result.breakdown.defective_fund} subtext="1%" />
                {result.breakdown.iva > 0 && (
                  <BreakdownRow label="IVA" value={result.breakdown.iva} subtext="19%" />
                )}
                {result.breakdown.retention > 0 && (
                  <BreakdownRow label="Retención" value={result.breakdown.retention} subtext="4%" />
                )}
                
                <div className="pt-2 mt-2 border-t border-border">
                  <BreakdownRow label="Total Costos" value={result.breakdown.total_costs} highlight />
                </div>
                
                <div className="mt-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-emerald-600 dark:text-emerald-400 font-medium">Tu Ganancia Neta</span>
                    <div className="text-right">
                      <span className="text-emerald-600 dark:text-emerald-400 font-bold text-xl">{formatCurrency(result.breakdown.profit)}</span>
                      <span className="text-emerald-600/70 dark:text-emerald-400/70 text-sm ml-2">({result.breakdown.actual_margin_pct}%)</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Tags informativos */}
              <div className="flex flex-wrap gap-2">
                {result.alerts.applies_free_shipping && (
                  <span className="px-2 py-1 bg-blue-500/20 text-blue-600 dark:text-blue-400 text-xs rounded-lg">
                    Aplica envío gratis
                  </span>
                )}
                {result.alerts.applies_unit_fee && (
                  <span className="px-2 py-1 bg-amber-500/20 text-amber-600 dark:text-amber-400 text-xs rounded-lg">
                    Aplica costo fijo
                  </span>
                )}
                {result.alerts.danger_zone && (
                  <span className="px-2 py-1 bg-red-500/20 text-red-600 dark:text-red-400 text-xs rounded-lg">
                    Zona de peligro
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Info de reglas Colombia */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <div className="p-3 bg-card/60 border border-border rounded-xl">
          <p className="text-xs text-muted-foreground mb-2 font-medium">Costo Fijo por Unidad</p>
          <div className="space-y-1 text-xs text-muted-foreground/70">
            <p>• &lt; $30.000: <span className="text-foreground">$2.500</span></p>
            <p>• $30.000 - $59.999: <span className="text-foreground">$4.000</span></p>
            <p>• ≥ $60.000: <span className="text-green-600 dark:text-green-400">$0</span></p>
          </div>
        </div>
        <div className="p-3 bg-card/60 border border-border rounded-xl">
          <p className="text-xs text-muted-foreground mb-2 font-medium">Envío Gratis Obligatorio</p>
          <div className="space-y-1 text-xs text-muted-foreground/70">
            <p>• Umbral: <span className="text-foreground">$60.000</span></p>
            <p>• Rep. Verde: <span className="text-green-600 dark:text-green-400">50% desc.</span></p>
            <p>• Rep. Amarilla: <span className="text-yellow-600 dark:text-yellow-400">25% desc.</span></p>
          </div>
        </div>
        <div className="p-3 bg-card/60 border border-border rounded-xl">
          <p className="text-xs text-muted-foreground mb-2 font-medium">Costos Ocultos</p>
          <div className="space-y-1 text-xs text-muted-foreground/70">
            <p>• Reserva Devoluciones: <span className="text-foreground">3%</span></p>
            <p>• Fondo Defectuosos: <span className="text-foreground">1%</span></p>
            <p className="text-[10px] text-muted-foreground/70">Se aplican sobre el precio</p>
          </div>
        </div>
      </div>
      
      {/* Disclaimer */}
      <div className="p-4 bg-cyan-500/10 border border-cyan-500/20 rounded-xl">
        <p className="text-xs text-cyan-700 dark:text-cyan-300">
          <strong>Nota:</strong> Las comisiones se consultan en tiempo real desde la API de MercadoLibre cuando es posible. 
          Los valores de envío son estimaciones basadas en tarifa 0.5kg. Los costos reales pueden variar según la categoría y el peso del producto.
        </p>
      </div>
    </div>
  );
};

// ========================================
// COMPONENTE PRINCIPAL: Expenses (con Tabs)
// ========================================
const Expenses = () => {
  const { user, loading, authToken } = useAuth();
  const navigate = useNavigate();
  
  // Actualizar formatCurrency con el site del usuario
  const userSite = user?.mercado_libre_site || 'MCO';
  formatCurrency = (v) => formatNumber(v, userSite);
  
  // Tab activo
  const [activeTab, setActiveTab] = useState('operativos');
  
  // Estado para Gastos Operativos
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loadingExpenses, setLoadingExpenses] = useState(true);

  // Estado para Impuestos
  const [taxes, setTaxes] = useState([]);
  
  // Estado para Costos de Producto
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  useEffect(() => {
    if (!loading && !user) navigate('/');
  }, [user, loading, navigate]);

  // Fetch Gastos Operativos
  const fetchExpenses = useCallback(async () => {
    setLoadingExpenses(true);
    try {
      const r = await apiClient.get('/api/expenses');
      setExpenses(r.data.expenses || []);
    } catch (e) { toast.error('Error al cargar gastos'); }
    finally { setLoadingExpenses(false); }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const r = await apiClient.get('/api/expenses/summary?period=last_30_days');
      setSummary(r.data);
    } catch (e) { console.error(e); }
  }, []);

  // Fetch Productos con CACHE
  const { fetchWithCache, invalidateCache } = useDataCache();
  
  const fetchProducts = useCallback(async (forceRefresh = false) => {
    setLoadingProducts(true);
    try {
      const data = await fetchWithCache(
        'products_lite',
        async () => {
          // Usar endpoint LITE optimizado para COGS (sin paginación, campos mínimos)
          const response = await apiClient.get('/api/products/lite');
          return response.data;
        },
        forceRefresh
      );
      setProducts(data.products || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar productos');
    } finally {
      setLoadingProducts(false);
    }
  }, [fetchWithCache]);

  const fetchTaxes = useCallback(async () => {
    try {
      const r = await apiClient.get('/api/tax-entries');
      setTaxes(r.data.taxes || []);
    } catch (e) { console.error(e); }
  }, []);

  const expensesLoadedRef = React.useRef(false);
  useEffect(() => {
    if (user && !expensesLoadedRef.current) {
      expensesLoadedRef.current = true;
      fetchExpenses();
      fetchSummary();
      fetchTaxes();
    }
  }, [user, fetchExpenses, fetchSummary, fetchTaxes]);
  
  // Lazy load: Cargar productos solo cuando el usuario va al tab de COGS
  // Usamos useRef para evitar re-ejecuciones infinitas
  const productsLoadedRef = React.useRef(false);
  
  useEffect(() => {
    if (activeTab === 'cogs' && !productsLoadedRef.current && products.length === 0) {
      productsLoadedRef.current = true;
      fetchProducts(false);
    }
  }, [activeTab, products.length, fetchProducts]);

  // Handlers para Gastos Operativos
  const handleAddExpense = async (data) => {
    try {
      await apiClient.post('/api/expenses', data);
      toast.success('Gasto registrado');
      fetchExpenses();
      fetchSummary();
    } catch (e) { toast.error('Error al guardar'); }
  };

  const handleDeleteExpense = async (id) => {
    try {
      await apiClient.delete('/api/expenses/' + id);
      toast.success('Eliminado');
      fetchExpenses();
      fetchSummary();
    } catch (e) { toast.error('Error al eliminar'); }
  };

  const handleEditExpense = async (id, data) => {
    try {
      await apiClient.put('/api/expenses/' + id, data);
      toast.success('Actualizado');
      fetchExpenses();
      fetchSummary();
    } catch (e) { toast.error('Error al actualizar'); }
  };

  const handleAddTax = async (data) => {
    try {
      await apiClient.post('/api/tax-entries', data);
      toast.success('Impuesto registrado');
      fetchTaxes();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar impuesto'); }
  };

  const handleDeleteTax = async (taxId) => {
    try {
      await apiClient.delete('/api/tax-entries/' + taxId);
      toast.success('Impuesto eliminado');
      fetchTaxes();
    } catch (e) { toast.error('Error al eliminar impuesto'); }
  };

  const handleCsvUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const r = await apiClient.post('/api/expenses/csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(r.data.created + ' gastos cargados');
      fetchExpenses();
      fetchSummary();
    } catch (e) { toast.error('Error al cargar'); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const r = await apiClient.get('/api/expenses/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_gastos.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) { toast.error('Error al descargar'); }
  };

  // Handlers para COGS
  const handleSaveCogs = async (itemId, variationId, newCogs, sku = '') => {
    try {
      await apiClient.post(`/api/products/${itemId}`, {
        cogs: newCogs,
        variation_id: variationId || null
      });
      setProducts(prev => prev.map(p => {
        if (p.item_id === itemId && (p.variation_id || null) === (variationId || null)) {
          return { ...p, cogs: newCogs };
        }
        return p;
      }));
      invalidateCache('products');
      // Formato de moneda para el toast
      const formattedCogs = new Intl.NumberFormat('es-CO', { 
        style: 'currency', 
        currency: 'COP', 
        minimumFractionDigits: 0 
      }).format(newCogs);
      toast.success(`Costo actualizado a ${formattedCogs} para SKU: ${sku || itemId}`);
    } catch (error) {
      toast.error('Error al guardar COGS - El producto debe existir en Mercado Libre');
    }
  };

  const handleUploadCogs = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await apiClient.post('/api/products/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const { updated_count, propagated_to_variations, total_in_file } = response.data;
      toast.success(`${updated_count + propagated_to_variations} productos actualizados`);
      invalidateCache('products'); // Invalidar cache
      fetchProducts(true); // Forzar refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar');
    }
  };

  // Solo mostrar loading inicial si el usuario aún no está cargado
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <nav className="bg-card/60 backdrop-blur-xl border-b border-border">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="text-muted-foreground hover:text-foreground" data-testid="back-costos">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
                <Layers className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                Gestión de Costos
              </h1>
              <p className="text-xs text-muted-foreground/70">Gastos operativos y costos de producto (COGS)</p>
            </div>
          </div>
          <TutorialVideoButton video="Modulo Costos.mp4" caption="Cómo registrar y analizar costos." />
        </div>
      </nav>

      <main className="container mx-auto px-6 py-6 max-w-6xl">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 bg-card/60 p-1.5 rounded-xl border border-border w-fit">
          <button
            onClick={() => setActiveTab('operativos')}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              activeTab === 'operativos'
                ? 'bg-rose-500/20 text-rose-600 dark:text-rose-400 border border-rose-500/30'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
            data-testid="tab-operativos"
          >
            <DollarSign className="w-4 h-4" />
            Gastos Operativos
          </button>
          <button
            onClick={() => setActiveTab('cogs')}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              activeTab === 'cogs'
                ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
            data-testid="tab-cogs"
          >
            <Package className="w-4 h-4" />
            Costos de Producto (COGS)
          </button>
          <div className="group relative">
            <button
              disabled
              className="px-5 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 text-muted-foreground/50 cursor-not-allowed"
              data-testid="tab-calculadora"
            >
              <PieChart className="w-4 h-4" />
              Calculadora
            </button>
            <div className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-popover px-2 py-1 text-xs text-foreground opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100">
              Próximamente
            </div>
          </div>
        </div>

        {/* Contenido según tab activo */}
        {activeTab === 'operativos' ? (
          <OperationalExpenses
            expenses={expenses}
            summary={summary}
            onAdd={handleAddExpense}
            onDelete={handleDeleteExpense}
            onEdit={handleEditExpense}
            onCsvUpload={handleCsvUpload}
            onDownloadTemplate={handleDownloadTemplate}
            taxes={taxes}
            onAddTax={handleAddTax}
            onDeleteTax={handleDeleteTax}
          />
        ) : activeTab === 'cogs' ? (
          <CostosProducto
            products={products}
            loadingProducts={loadingProducts}
            onSaveCogs={handleSaveCogs}
            onUploadCogs={handleUploadCogs}
            onRefresh={() => fetchProducts(true)}
          />
        ) : (
          <CalculadoraRentabilidad />
        )}
      </main>
    </div>
  );
};

export default Expenses;
